import { Component, OnInit, OnDestroy, Input, ViewChild } from "@angular/core";
import { JobsService } from "@/services/jobs/jobs.service";
import { BuildersService } from "@/services/builders/builders.service";
import { CommunitiesService } from "@/services/communities/communities.service";
import { ContactsService } from "@/services/contacts/contacts.service";
import { DataTableDirective } from "angular-datatables";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { Select2OptionData } from "ng-select2";
import { Observable, forkJoin } from "rxjs";
import { EmployeesService } from '@/services/employees/employees.service';

@Component({
	selector: "datatable-builder-jobs",
	templateUrl: "./builder-jobs-datatable.component.html",
	styleUrls: ["./builder-jobs-datatable.component.scss"],
})
export class BuilderJobsDatatableComponent implements OnInit, OnDestroy {
	@ViewChild(DataTableDirective)
	datatableElement: DataTableDirective;

	@Input() title: string = "";
	@Input() showFooter: boolean = true;
	@Input() rowsPerPage: number = 10;
	@Input() viewRoute: string = "";
	@Input() viewText: string = "View";
	@Input() status: string = "all";
	@Input() showStatus: boolean = true;
	@Input() showFilters: boolean = true;

	windowLoc = "";
	rows = [];
	dtOptions: DataTables.Settings = {};
	builderMap = {};
	communityMap = {};
	contactMap = {};
	lookupDataReady = false;
	showTitle: Boolean = false;
	activeStatusFilter: string = "";
	activeCrewLeaderFilter: string = "";
	crewLeaders: Observable<Array<Select2OptionData>>;

	jobStatuses: string[] = ["Active", "Closed", "Removed"];

	select2Options = {
		theme: "bootstrap4",
		dropdownAutoWidth: true,
		allowClear: true,
		width: "100%",
	};

	selectStatusFilter: Array<Select2OptionData> = [
		{
			id: "",
			text: "",
		},
		{
			id: "active",
			text: "Active",
		},
		{
			id: "closed",
			text: "Closed",
		},
		{
			id: "removed",
			text: "Removed",
		},
	];

	constructor(
		private jobsService: JobsService,
		private buildersService: BuildersService,
		private communitiesService: CommunitiesService,
		private contactsService: ContactsService,
		private employeeService: EmployeesService,
		private config: NgbPopoverConfig,
	) {
		config.placement = "left";
	}

	dataTablesInit(searchStr?: any) {
		let lastPage = 0;
		let lastSearchText = "";
		let searchOptions: DataTables.SearchSettings = { search: searchStr };
		this.dtOptions = {
			pagingType: "full_numbers",
			pageLength: this.rowsPerPage,
			lengthChange: false,
			displayStart: lastPage, // Last Selected Page
			info: this.showFooter,
			paging: this.showFooter,
			scrollX: true,
			search: searchOptions,
			columns: [
				{ data: "CreatedDate" },
				{ data: "ScheduledDate" },
				{
					data: "Builder",
					render: function (data, type, row) {
						return "CompanyName" in data ? data.CompanyName : "";
					},
				},
				{
					data: "Community",
					render: function (data, type, row) {
						return "Name" in data ? data.Name : "";
					},
				},
				{
					data: "Address",
					render: function (data, type, row) {
						return (
							data +
							" " +
							(row.Address2 != null ? row.Address2 : "") +
							"<br />" +
							row.City +
							", " +
							row.State +
							" " +
							row.ZipCode
						);
					},
				},
				{
					data: "CommunityManagers",
					render: function (data, type, row) {
						return data.length > 0 ? data.join("<br />") : "";
					},
					orderable: false,
				},
				{
					data: "Status",
					visible: this.showStatus,
					orderable: false,
				},
				{
					data: "Inspections",
					render: function (data, type, row) {
						return "";
					},
				},
				{
					data: "Detail",
					render: function (data, type, row) {
						return "";
					},
				},
				{
					data: "CrewLeader",
					visible: false,
				},
			],
			columnDefs: [
				{
					targets: this.showStatus ? [7, 8] : [6, 7],
					orderable: false,
					searchable: false,
				},
			],
			serverSide: true,
			processing: true,
			ajax: (data: object, callback, dataTablesParameters: any) => {
				// console.log('bj datatables', data, dataTablesParameters);
				lastPage = dataTablesParameters.start; // Note :  dataTablesParameters.start = page count * table length
				lastSearchText =
					"search" in dataTablesParameters &&
					dataTablesParameters.search != "undefined"
						? dataTablesParameters.search.value
						: "";

				if (this.activeStatusFilter !== "") {
					data["columns"][6]["search"].value =
						this.activeStatusFilter.toLowerCase();
				}
				if (this.activeCrewLeaderFilter !== "") {
					data["columns"][9]["search"].value =
						this.activeCrewLeaderFilter.toLowerCase();
				}

				//this.status, this.rowsPerPage, lastPage
				this.jobsService
					.getBuilderJobsDatatable(data)
					.subscribe((result: any) => {
						// console.log('result', result);
						// Get all Rows
						result.data.forEach((row) => {
							let communityManagers: Array<any> = [];

							row.Builder = this.builderMap[row.BuilderID] || null;
							row.Community = this.communityMap[row.CommunityID] || null;
							if (row.Community && row.Community.ManagerIDs) {
								row.Community.ManagerIDs.forEach((id: string) => {
									const contact = this.contactMap[id];
									if (contact) {
										communityManagers.push({
											name: contact.Name,
											id: contact.Id,
										});
									}
								});
							}

							row["DryinChecklistAddLink"] =
								`/builder-jobs/${row.ID}/dryin-inspection-add`;
							row["ShingleChecklistAddLink"] =
								`/builder-jobs/${row.ID}/shingle-inspection-add`;
							row["SafetyChecklistAddLink"] =
								`/builder-jobs/${row.ID}/safety-inspection-add`;

							if (row.Inspections.length) {
								row.Inspections.sort(function (a, b) {
									const c: any = new Date(a.CreatedDate);
									const d: any = new Date(b.CreatedDate);
									return c - d;
								});
							}

							row["Lots"] = [row.Lot1, row.Lot2].filter(Boolean).join("/");
							let status = row.Status;

							// Set Default status color
							row.StatusColor = "dark";

							switch (status) {
								case "Active":
									row.StatusColor = "success";
									break;
								case "Closed":
									row.StatusColor = "danger";
									break;
							}

							row.CommunityManagers = communityManagers;
						});

						// Store rows in class property
						this.rows = result.data;
						callback({
							recordsTotal: result.recordsTotal,
							recordsFiltered: result.recordsFiltered,
							data: [], // this gets set to empty because this.rows above is rendered by angular with the relevant formatting
						});
						$($.fn.dataTable.tables(true)).DataTable().columns.adjust();

						setTimeout(() => {
							$($.fn.dataTable.tables(true)).DataTable().columns.adjust();
						}, 2500);
					});
			},
			order: [[0, "desc"]],
			initComplete: () => {
				let searchLabels = document.querySelectorAll(
					".dataTables_filter > label",
				);

				searchLabels.forEach((label) => {
					label.setAttribute("aria-label", "Search/Filter Table");
				});

				this.showTitle = true;
			},
		};

		setTimeout(() => {
			$($.fn.dataTable.tables(true)).DataTable().columns.adjust();
		}, 2000);
	}

	ngOnInit() {
		this.windowLoc = window.location.pathname;
		let trySearch: any = window.sessionStorage.getItem(
			this.windowLoc + "builderJobSearch",
		);
		window.sessionStorage.clear();

		// Preload lookup data in bulk
	forkJoin([
		this.buildersService.getBuilders(),
		this.communitiesService.getCommunities(),
		this.contactsService.getContacts()
	]).subscribe((lookups: any) => {
		lookups[0].body.forEach(b => this.builderMap[b.ID] = b);
		lookups[1].body.forEach(c => this.communityMap[c.ID] = c);
		lookups[2].body.forEach(c => this.contactMap[c.Id] = c);
		this.lookupDataReady = true;
	});

	this.employeeService.getEmployees().subscribe( (result: any) => {
			let employees = result.body
				.map( employee => {
					return {
						text: `${ employee.FirstName.trim() } ${ employee.LastName.trim() }`,
						id: employee.ID
					}
				});

			employees.sort(function(a, b){ return b.text > a.text ? -1 : 1; });
			employees.unshift({ id: "", text: "" });
			this.crewLeaders = this.updateUsers(employees)
		});

		if (trySearch != null && trySearch != "") {
			this.dataTablesInit(trySearch);
		} else {
			this.dataTablesInit();
		}
	}

	ngOnDestroy(): void {
		let dtinst: DataTables.Api;
		let search: any = "";

		this.datatableElement.dtInstance.then((x) => {
			dtinst = x;
			search = dtinst.search();
			if (search != null || search != "") {
				window.sessionStorage.setItem(
					this.windowLoc + "builderJobSearch",
					search,
				);
			}
		});
	}

	updateStatusFilter(event: { value: string; data }) {
		const { value } = event;

		this.activeStatusFilter = value;

		this.datatableElement.dtInstance.then((dtInstance: DataTables.Api) => {
			dtInstance.search("");
			dtInstance.columns().search("");
			dtInstance.draw();
		});
	}

	updateCrewLeaderFilter(event: { value: string; data }) {
		const { value } = event;

		this.activeCrewLeaderFilter = value;

		this.datatableElement.dtInstance.then((dtInstance: DataTables.Api) => {
			dtInstance.search("");
			dtInstance.columns().search("");
			dtInstance.draw();
		});
	}

	updateUsers(users = []): Observable<Array<Select2OptionData>> {
		return Observable.create((obs) => {
			obs.next(users);
			obs.complete();
		});
	}
}
